import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { lightTheme, darkTheme } from './theme';
import { Layout } from './components/layout/Layout';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { BtBillReconPage } from './pages/dashboard/BtBillReconPage';
import { HealthCheckPage } from './pages/dashboard/HealthCheckPage';
import { SecurityMonitorPage } from './pages/dashboard/SecurityMonitorPage';
import { DestinationFiltersPage } from './pages/routing/DestinationFiltersPage';
import { AuditLogPage } from './pages/audit/AuditLogPage';
import { CustomerAccountsPage } from './pages/accounts/CustomerAccountsPage';
import { L2_PATHS } from './store/auth';
import { CdrPage } from './pages/cdr/CdrPage';
import { CallReportsPage } from './pages/reports/CallReportsPage';
import { SummaryReportPage } from './pages/reports/SummaryReportPage';
import { RevenueAssurancePage } from './pages/reports/RevenueAssurancePage';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
import { TenantsPage } from './pages/tenants/TenantsPage';
import { CustomerDetailPage } from './pages/tenants/CustomerDetailPage';
import { CompliancePage } from './pages/compliance/CompliancePage';
import { UsersPage } from './pages/users/UsersPage';
import { CarriersPage } from './pages/carriers/CarriersPage';
import { RatesPage } from './pages/rates/RatesPage';
import { RateCardPage } from './pages/rates/RateCardPage';
import { BillingPage } from './pages/billing/BillingPage';
import { FraudPage } from './pages/fraud/FraudPage';
import { SbcPage } from './pages/sbc/SbcPage';
import { SbcControlPage } from './pages/sbc/SbcControlPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { NumbersPage } from './pages/numbers/NumbersPage';
import { BlockedNumbersPage } from './pages/numbers/BlockedNumbersPage';
import { UnassignedNumbersPage } from './pages/numbers/UnassignedNumbersPage';
import { TrunksAndDevicesPage } from './pages/sipdevices/TrunksAndDevicesPage';
import { TicketsPage } from './pages/tickets/TicketsPage';
import { AclPage } from './pages/acl/AclPage';
import { BillingImportPage } from './pages/import/BillingImportPage';
import { PaymentSettingsPage } from './pages/payments/PaymentSettingsPage';
import { CustomerDashboardPage } from './pages/customer/CustomerDashboardPage';
import { CustomerCDRPage } from './pages/customer/CustomerCDRPage';
import { CustomerBalancePage } from './pages/customer/CustomerBalancePage';
import { ProductCataloguePage } from './pages/subscriptions/ProductCataloguePage';
import { SubscriptionsPage } from './pages/subscriptions/SubscriptionsPage';
import { LiveCallsPage } from './pages/calls/LiveCallsPage';
import { FailuresPage } from './pages/calls/FailuresPage';
import { ComplianceMonitorPage } from './pages/monitor/ComplianceMonitorPage';
import { LogViewerPage } from './pages/calls/LogViewerPage';
import { BundleUsagePage } from './pages/subscriptions/BundleUsagePage';
import { BillingRunPage } from './pages/subscriptions/BillingRunPage';
import { CustomerOnboardingPage } from './pages/onboarding/CustomerOnboardingPage';
import { CarrierOnboardingPage } from './pages/onboarding/CarrierOnboardingPage';
import { AiManagementPage } from './pages/ai/AiManagementPage';
import { BillingContactsPage } from './pages/billing-contacts/BillingContactsPage';
import { NotificationsPage } from './pages/notifications/NotificationsPage';
import { SecurityPage } from './pages/profile/SecurityPage';
import { CarrierViewerShell } from './pages/carrier/CarrierViewerShell';
import { useAuthStore } from './store/auth';

const RoleRedirect: React.FC = () => {
  const user = useAuthStore(s => s.user);
  const isCustomer = user?.role === 'customer_admin' || user?.role === 'customer_user';
  return <Navigate to={isCustomer ? '/customer/dashboard' : '/dashboard'} replace />;
};

// A carrier viewer (e.g. "BT User") is a single-purpose read-only account: it gets
// ONLY the standalone carrier monitor — no sidebar, no other pages. Everyone else
// gets the normal app shell (Layout + full routes).
const AuthedShell: React.FC<{ darkMode: boolean; onToggleDark: () => void; children: React.ReactNode }> = ({ darkMode, onToggleDark, children }) => {
  const role = useAuthStore(s => s.user?.role);
  const { pathname } = useLocation();
  if (role === 'carrier_viewer') return <CarrierViewerShell />;
  // L2 Engineer: only the monitoring pages; anything else goes back to the dashboard.
  if (role === 'l2_engineer' && !L2_PATHS.has(pathname)) return <Navigate to='/dashboard' replace />;
  return <Layout darkMode={darkMode} onToggleDark={onToggleDark}>{children}</Layout>;
};



const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAuthStore(s => s.token);
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const location = useLocation();

  if (!token || !user || !isAuthenticated()) return <Navigate to='/login' replace />;


  return <>{children}</>;
};

const SessionMonitor: React.FC = () => {
  const navigate = useNavigate();
  const checkSession = useAuthStore(s => s.checkSession);
  const token = useAuthStore(s => s.token);

  useEffect(() => {
    const updateActivity = () => {
      if (localStorage.getItem('access_token')) {
        localStorage.setItem('last_activity', Date.now().toString());
      }
    };
    window.addEventListener('mousemove', updateActivity, { passive: true });
    window.addEventListener('keydown', updateActivity, { passive: true });
    window.addEventListener('click', updateActivity, { passive: true });
    window.addEventListener('touchstart', updateActivity, { passive: true });

    const interval = setInterval(() => {
      if (token && !checkSession()) {
        navigate('/login', { replace: true });
      }
    }, 60 * 1000);

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
      clearInterval(interval);
    };
  }, [checkSession, navigate, token]);

  return null;
};

const App: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false);

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <BrowserRouter>
          <SessionMonitor />
          <Routes>
            <Route path='/login' element={<LoginPage />} />
            <Route path='/*' element={
              <ProtectedRoute>
                <AuthedShell darkMode={darkMode} onToggleDark={() => setDarkMode(!darkMode)}>
                  <Routes>
                    <Route path='/' element={<RoleRedirect />} />
                    <Route path='/dashboard' element={<DashboardPage />} />
                    <Route path='/dashboard/bt-bill' element={<BtBillReconPage />} />
                    <Route path='/dashboard/health' element={<HealthCheckPage />} />
                    <Route path='/dashboard/health/security' element={<SecurityMonitorPage />} />
                    <Route path='/routing/destination-filters' element={<DestinationFiltersPage />} />
                    <Route path='/cdr' element={<CdrPage />} />
                    <Route path='/reports/customer-calls' element={<CallReportsPage />} />
                    <Route path='/reports/customer-summary' element={<SummaryReportPage group='customer' />} />
                    <Route path='/reports/trunk-summary' element={<SummaryReportPage group='trunk' />} />
                    <Route path='/reports/country' element={<SummaryReportPage group='country' />} />
                    <Route path='/reports/revenue-assurance' element={<RevenueAssurancePage />} />
                    <Route path='/analytics' element={<AnalyticsPage />} />
                    <Route path='/tenants' element={<TenantsPage />} />
                    <Route path='/customers/:id' element={<CustomerDetailPage />} />
                    <Route path='/compliance' element={<CompliancePage />} />
                    <Route path='/users' element={<UsersPage />} />
                    <Route path='/carriers' element={<CarriersPage />} />
                    <Route path='/rates' element={<RatesPage />} />
                    <Route path='/rate-card' element={<RateCardPage />} />
                    <Route path='/billing' element={<BillingPage />} />
                    <Route path='/fraud' element={<FraudPage />} />
                    <Route path='/calls/live' element={<LiveCallsPage />} />
                    <Route path='/monitoring/failures' element={<FailuresPage />} />
                    <Route path='/monitoring/compliance' element={<ComplianceMonitorPage />} />
                    <Route path='/calls/logs' element={<LogViewerPage />} />
                    <Route path='/sbc' element={<SbcPage />} />
                    <Route path='/sbc-control' element={<SbcControlPage />} />
                    <Route path='/invoices' element={<InvoicesPage />} />
                    <Route path='/billing-contacts' element={<BillingContactsPage />} />
                    <Route path='/numbers' element={<NumbersPage />} />
                    <Route path='/numbers/unassigned' element={<UnassignedNumbersPage />} />
                    <Route path='/numbers/blocked' element={<BlockedNumbersPage />} />
                    <Route path='/sip-devices' element={<TrunksAndDevicesPage />} />
                    <Route path='/tickets' element={<TicketsPage />} />
                    <Route path='/acl' element={<AclPage />} />
                    <Route path='/payment-settings' element={<PaymentSettingsPage />} />
                    <Route path='/billing-import' element={<BillingImportPage />} />
                    <Route path='/catalogue' element={<ProductCataloguePage />} />
                    <Route path='/subscriptions' element={<SubscriptionsPage />} />
                    <Route path='/bundle-usage' element={<BundleUsagePage />} />
                    <Route path='/billing-run' element={<BillingRunPage />} />
                    <Route path='/onboarding' element={<CustomerOnboardingPage />} />
                    <Route path='/onboarding/carrier' element={<CarrierOnboardingPage />} />
                    <Route path='/security' element={<SecurityPage />} />
                    <Route path='/ai-assistant' element={<AiManagementPage />} />
                    <Route path='/audit' element={<AuditLogPage />} />
                    <Route path='/accounts' element={<CustomerAccountsPage />} />
                    <Route path='/notifications' element={<NotificationsPage />} />
                    <Route path='/customer/dashboard' element={<CustomerDashboardPage />} />
                    <Route path='/customer/cdrs' element={<CustomerCDRPage />} />
                    <Route path='/customer/balance' element={<CustomerBalancePage />} />
                    <Route path='/customer/invoices' element={<InvoicesPage />} />
                  </Routes>
                </AuthedShell>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
